import { closestCenter, DndContext, type DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveIcon from '@mui/icons-material/Save';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Grid,
    IconButton,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useDialog } from 'muibox';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useState } from 'react';
import useAxios from '../../hooks/useAxios';
import useMenu, { type PpvMenuCategory } from '../../hooks/useMenu';
import { SettingsContext } from '../../store/SettingsContext';

type CategoryFormState = {
    slug: string;
    name: string;
    description: string;
    sortOrder: number;
    active: boolean;
};

const defaultForm: CategoryFormState = {
    slug: '',
    name: '',
    description: '',
    sortOrder: 0,
    active: true,
};

const toForm = (c: PpvMenuCategory): CategoryFormState => ({
    slug: c.slug,
    name: c.name,
    description: c.description || '',
    sortOrder: c.sortOrder,
    active: c.active,
});

const SortableCategoryRow = (props: {
    category: PpvMenuCategory;
    onEdit: (category: PpvMenuCategory) => void;
    onDelete: (category: PpvMenuCategory) => void;
}) => {
    const { category, onEdit, onDelete } = props;
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: category.id,
    });

    return (
        <Stack
            ref={setNodeRef}
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{
                py: 0.5,
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.55 : 1,
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                    ref={setActivatorNodeRef}
                    component="span"
                    {...attributes}
                    {...listeners}
                    sx={{ display: 'inline-flex', cursor: 'grab', color: 'text.secondary', '&:active': { cursor: 'grabbing' } }}
                >
                    <DragIndicatorIcon fontSize="small" />
                </Box>
                <Chip size="small" label={category.active ? 'Active' : 'Inactive'} color={category.active ? 'success' : 'default'} />
                <Chip size="small" variant="outlined" label={`/categories/${category.slug}`} />
                <Typography variant="body2" noWrap title={category.name}>
                    {category.name}
                </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5}>
                <Chip size="small" variant="outlined" label={`${category.menuItemsCount ?? 0} items`} />
                <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => onEdit(category)}>
                        <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => onDelete(category)}>
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Stack>
    );
};

const AdminOnlyPromoteCategories = () => {
    const axios = useAxios();
    const dialog = useDialog();
    const settingsContext = useContext(SettingsContext);
    const { menuCategoriesQuery } = useMenu();

    const [form, setForm] = useState<CategoryFormState>(defaultForm);
    const [editing, setEditing] = useState<PpvMenuCategory | null>(null);
    const [saving, setSaving] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);
    const [orderedCategories, setOrderedCategories] = useState<PpvMenuCategory[]>([]);

    const categories = useMemo(() => menuCategoriesQuery.data ?? [], [menuCategoriesQuery.data]);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    useEffect(() => {
        const next = [...categories].sort((a, b) => {
            const d = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
            if (d !== 0) return d;
            return a.id - b.id;
        });
        setOrderedCategories(next);
    }, [categories]);

    const canSave = form.slug.trim().length > 0 && form.name.trim().length > 0 && !saving;

    const resetForm = () => {
        setForm(defaultForm);
        setEditing(null);
    };

    const saveCategory = async () => {
        if (!canSave) {
            return;
        }

        setSaving(true);
        const payload = {
            slug: form.slug.trim().toLowerCase(),
            name: form.name.trim(),
            description: form.description.trim() || null,
            sortOrder: Math.max(0, form.sortOrder),
            active: form.active,
        };

        try {
            const endpoint = settingsContext.routes.menu.categories;
            const response = editing ? await axios.put(`${endpoint}/${editing.id}`, payload) : await axios.post(endpoint, payload);

            if (!response?.data) {
                enqueueSnackbar('Unable to save category', { variant: 'error' });
                return;
            }

            await menuCategoriesQuery.refetch();
            enqueueSnackbar(editing ? 'Category updated' : 'Category created', { variant: 'success' });
            resetForm();
        } catch (error) {
            enqueueSnackbar('Unable to save category', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const deleteCategory = async (category: PpvMenuCategory) => {
        await dialog
            .confirm({
                title: 'Delete category?',
                message: `This will deactivate ${category.name}.`,
                ok: { color: 'error', text: 'Delete', variant: 'contained' },
            })
            .then(async () => {
                try {
                    await axios.delete(`${settingsContext.routes.menu.categories}/${category.id}`);
                    await menuCategoriesQuery.refetch();
                    enqueueSnackbar('Category deleted', { variant: 'success' });
                    if (editing?.id === category.id) {
                        resetForm();
                    }
                } catch (error) {
                    enqueueSnackbar('Unable to delete category', { variant: 'error' });
                }
            })
            .catch(() => null);
    };

    const onDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setOrderedCategories(prev => {
            const oldIndex = prev.findIndex(item => item.id === active.id);
            const newIndex = prev.findIndex(item => item.id === over.id);
            if (oldIndex < 0 || newIndex < 0) return prev;
            return arrayMove(prev, oldIndex, newIndex);
        });
    };

    const saveOrder = async () => {
        setSavingOrder(true);
        try {
            const endpoint = settingsContext.routes.menu.categories;
            for (let i = 0; i < orderedCategories.length; i += 1) {
                const c = orderedCategories[i];
                if ((c.sortOrder ?? 0) === i) continue;
                // eslint-disable-next-line no-await-in-loop
                await axios.put(`${endpoint}/${c.id}`, { sortOrder: i });
            }
            await menuCategoriesQuery.refetch();
            enqueueSnackbar('Category order updated', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Unable to update category order', { variant: 'error' });
        } finally {
            setSavingOrder(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ pt: 3, pb: 4 }}>
            <Stack spacing={2}>
                <Typography variant="h5">onlypromote.me Categories</Typography>
                <Typography variant="body2" color="text.secondary">
                    Manage public PPV menu categories used for public discovery and filtering.
                </Typography>

                <Card variant="outlined">
                    <CardContent>
                        <Stack spacing={2}>
                            <Typography variant="h6">{editing ? 'Edit Category' : 'Add Category'}</Typography>
                            <Grid container spacing={1}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label="Slug"
                                        value={form.slug}
                                        onChange={e => setForm(prev => ({ ...prev, slug: e.target.value }))}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <TextField
                                        label="Name"
                                        value={form.name}
                                        onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        label="Sort Order"
                                        type="number"
                                        inputProps={{ min: 0 }}
                                        value={form.sortOrder}
                                        onChange={e =>
                                            setForm(prev => ({
                                                ...prev,
                                                sortOrder: Math.max(0, Number.parseInt(e.target.value || '0', 10) || 0),
                                            }))
                                        }
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Description"
                                        value={form.description}
                                        onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                                        fullWidth
                                        multiline
                                        minRows={2}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="body2">Active</Typography>
                                            <Switch
                                                checked={form.active}
                                                onChange={e => setForm(prev => ({ ...prev, active: e.target.checked }))}
                                            />
                                        </Stack>
                                        <Stack direction="row" spacing={1}>
                                            {editing && (
                                                <Button variant="outlined" color="inherit" onClick={resetForm} disabled={saving}>
                                                    Cancel Edit
                                                </Button>
                                            )}
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                startIcon={editing ? <SaveIcon /> : <AddIcon />}
                                                onClick={saveCategory}
                                                disabled={!canSave}
                                            >
                                                {editing ? 'Save Changes' : 'Add Category'}
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Stack>
                    </CardContent>
                </Card>

                <Card variant="outlined">
                    <CardContent>
                        <Stack spacing={1}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="h6">Categories ({categories.length})</Typography>
                                <Button variant="outlined" onClick={saveOrder} disabled={savingOrder || orderedCategories.length < 2}>
                                    {savingOrder ? 'Saving order...' : 'Save order'}
                                </Button>
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                Drag by the handle to reorder categories.
                            </Typography>
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                                <SortableContext items={orderedCategories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                                    {orderedCategories.map(category => (
                                        <SortableCategoryRow
                                            key={category.id}
                                            category={category}
                                            onEdit={c => {
                                                setEditing(c);
                                                setForm(toForm(c));
                                            }}
                                            onDelete={deleteCategory}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>
                            {categories.length === 0 && <Typography variant="body2">No categories found.</Typography>}
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
        </Container>
    );
};

export default AdminOnlyPromoteCategories;
